import { useIsFetching } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

// Background pollers we don't want to surface as a foreground "loading" cue:
// the import-job and import-usage queries refetch on their own intervals and
// would otherwise flash the indicator every few seconds.
const BACKGROUND_QUERY_ROOTS: ReadonlySet<string> = new Set([
  "import-jobs",
  "import-usage",
]);

// Global data-loading cue (#1). Paired with the query client's
// `placeholderData: keepPreviousData` default, the previously loaded data
// stays on screen while a refetch is in flight; this small centered pill is
// the only thing that changes, so navigating or changing filters no longer
// blanks the page. Returns nothing when no foreground query is fetching.
export function GlobalLoadingIndicator() {
  const fetching = useIsFetching({
    predicate: (query) => {
      const root = query.queryKey[0];
      return typeof root !== "string" || !BACKGROUND_QUERY_ROOTS.has(root);
    },
  });
  if (fetching === 0) {
    return null;
  }
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="global-loading-indicator"
      className="pointer-events-none fixed inset-0 z-50 grid place-items-center"
    >
      <span className="flex items-center gap-3 rounded-2xl border border-outline-variant bg-surface/95 px-6 py-4 text-sm font-medium text-on-surface shadow-lg backdrop-blur-sm animate-in fade-in-0 zoom-in-95 duration-200 motion-reduce:animate-none">
        <Loader2
          aria-hidden="true"
          className="h-6 w-6 animate-spin text-primary motion-reduce:animate-none"
        />
        Loading&hellip;
      </span>
    </div>
  );
}
